import type { ComponentProps, CSSProperties } from "react";
import { Toaster as Sonner } from "sonner";
import { IconTile } from "#components/icon-tile";
import {
  CheckCircleIcon,
  CheckIcon,
  CircleNotchIcon,
  InfoIcon,
  WarningIcon,
  WifiSlashIcon,
} from "#components/icons";
import { cn } from "#lib/utils";

/** A failed write's mark: the red tile's hue in a circle, with the offline glyph when the
 * connection never carried the request. */
function FailureMark({ offline = false }: { readonly offline?: boolean }) {
  const Glyph = offline ? WifiSlashIcon : WarningIcon;
  return (
    <IconTile hue="red" className="rounded-full">
      <Glyph />
    </IconTile>
  );
}

/** A finished write's mark: the done check, as a task's own check draws it. */
function DoneMark() {
  return (
    <span className="tile inline-flex size-[var(--tile-size)] items-center justify-center rounded-full [--tile-from:var(--done-from)] [--tile-to:var(--done-to)]">
      <CheckIcon className="size-[var(--icon-size-sm)]" />
    </span>
  );
}

/** The one look for a toast's own button, so an action reads on the dark pill. */
const toastAction =
  "inline-flex h-[var(--control-h-sm)] shrink-0 items-center rounded-full bg-toast-action px-[var(--control-px-sm)] text-body font-bold text-toast-foreground outline-none transition-transform duration-[var(--duration-fast)] ease-ui focus-visible:focus-ring active:scale-[0.98]";

/** A card held at the bottom edge sets `--toast-above` to its top, so toasts stack above it. */
const LIFT =
  "max(var(--toast-offset), calc(var(--toast-above, 0px) + var(--toast-card-gap)))";

// Unstyled, so the pill is drawn from tokens alone. A toast hugs its line up to the toaster's width, and its
// line is cut short rather than wrapped; the app lets a failure's wrap. Up to 600px sonner widens
// the stack to the window past its left inset, hence the overrides there. Wide, the stack centres on `--toast-x` rather than the
// window, so it never lands on a side panel. Both widths rise by `--toast-offset`.
function Toaster({
  className,
  style,
  ...props
}: ComponentProps<typeof Sonner>) {
  return (
    <Sonner
      className={cn(
        "toaster group md:data-[x-position=center]:left-(--toast-x)! [@media(max-width:600px)]:w-auto!",
        className,
      )}
      icons={{
        success: <CheckCircleIcon className="size-[var(--icon-size)]" />,
        info: <InfoIcon className="size-[var(--icon-size)]" />,
        warning: <WarningIcon className="size-[var(--icon-size)]" />,
        error: <FailureMark />,
        loading: (
          <CircleNotchIcon className="size-[var(--icon-size)] animate-spin" />
        ),
      }}
      offset={{ bottom: LIFT }}
      mobileOffset={{ bottom: LIFT }}
      toastOptions={{
        unstyled: true,
        classNames: {
          toast:
            "right-0 left-0 mx-auto [@media(max-width:600px)]:w-full! flex min-h-[var(--size-touch)] w-fit max-w-full items-center gap-2.5 rounded-full border border-toast-edge bg-linear-to-b from-toast-from to-toast-to py-1.5 pr-1.5 pl-4 font-sans text-body text-toast-foreground shadow-lift-3",
          icon: "flex shrink-0 items-center",
          content: "min-w-0 flex-1",
          title: "truncate font-medium",
        },
      }}
      // CSSProperties has no index for "--*" keys, hence the cast.
      style={{ "--width": "var(--toast-shown-w)", ...style } as CSSProperties}
      {...props}
    />
  );
}

export { DoneMark, FailureMark, Toaster, toastAction };
